import { ArrowDown, Workflow, FlaskConical } from "lucide-react";
import { ARCH_INTEGRATIONS, ARCH_STAGES } from "@/lib/partnership";

const FIELDS = [
  { key: "read", label: "Data read by O.N.E.Tech" },
  { key: "output", label: "Decision / output produced" },
  { key: "writeback", label: "Data written back" },
  { key: "capability", label: "Required API / webhook capability" },
];

export default function Architecture() {
  return (
    <div className="space-y-8" data-testid="architecture-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <Workflow className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Integration Architecture
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          The simulated end-to-end flow from broker systems to outcome attribution. Every connector shown here is a
          demonstration simulation — no live systems are connected.
        </p>
      </div>

      <div className="max-w-3xl" data-testid="architecture-flow">
        {ARCH_STAGES.map((s, i) => (
          <div key={s.n}>
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 flex gap-4 hover:border-emerald-500/30 transition-colors duration-200" data-testid={`arch-stage-${s.n}`}>
              <div className="w-9 h-9 shrink-0 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center font-mono font-semibold text-emerald-400 text-sm">
                {s.n}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100">{s.title}</div>
                <p className="text-xs text-zinc-400 leading-relaxed mt-1">{s.desc}</p>
                {s.chips && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {s.chips.map((chip) => (
                      <span key={chip} className="px-2 py-0.5 rounded-full text-[10px] font-mono border border-zinc-700 bg-zinc-800/60 text-zinc-300">{chip}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {i < ARCH_STAGES.length - 1 && (
              <div className="flex justify-start pl-9 py-1">
                <ArrowDown className="w-4 h-4 text-emerald-500/60" strokeWidth={1.5} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-xl font-bold tracking-tight text-zinc-100 mb-1">Per-Integration Detail</h2>
        <p className="text-xs text-zinc-500 mb-4">What each connector reads, produces and writes back — and the capability it requires in production.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ARCH_INTEGRATIONS.map((intg) => (
            <div key={intg.key} className="bg-zinc-900 border border-zinc-800 rounded-md p-5 flex flex-col gap-4" data-testid={`arch-integration-${intg.key}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-semibold text-zinc-100">{intg.name}</h3>
                <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-400 whitespace-nowrap" data-testid={`arch-status-${intg.key}`}>
                  <FlaskConical className="w-3 h-3" strokeWidth={1.5} />
                  Simulated for Demonstration
                </span>
              </div>
              <div className="space-y-3">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <div className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">{f.label}</div>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-0.5">{intg[f.key]}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-amber-500/20 bg-amber-500/5 rounded-sm px-4 py-3 text-[11px] font-mono text-amber-500/90" data-testid="architecture-disclaimer">
        This architecture is illustrative. No existing integration with any named provider is claimed; production connectors are scoped during technical discovery.
      </div>
    </div>
  );
}
